import { useEffect, useRef, useState } from "react";
import { Eraser } from "lucide-react";

export default function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const inkRef = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const c = canvasRef.current;
    const ratio = window.devicePixelRatio || 1;
    c.width = c.offsetWidth * ratio;
    c.height = c.offsetHeight * ratio;
    const ctx = c.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#312e81";
  }, []);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - r.left, y: p.clientY - r.top };
  };

  const start = (e) => {
    drawing.current = true;
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    e.preventDefault();
  };

  const move = (e) => {
    if (!drawing.current) return;
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    inkRef.current = true;
    setHasInk(true);
    e.preventDefault();
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (inkRef.current) onChange?.(canvasRef.current.toDataURL("image/png"));
  };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.restore();
    drawing.current = false;
    inkRef.current = false;
    setHasInk(false);
    onChange?.(null);
  };

  return (
    <div data-testid="signature-pad">
      <div className="relative border border-dashed border-steel-border bg-white">
        <canvas
          ref={canvasRef}
          data-testid="signature-canvas"
          className="h-40 w-full cursor-crosshair touch-none"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          onTouchCancel={end}
        />
        {!hasInk && (
          <p className="pointer-events-none absolute inset-0 flex items-center justify-center font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
            Sign here with mouse or finger
          </p>
        )}
      </div>
      <button
        type="button"
        data-testid="signature-clear-btn"
        onClick={clear}
        className="mt-2 flex items-center gap-1.5 text-xs text-steel-muted transition-colors hover:text-steel-text"
      >
        <Eraser className="h-3.5 w-3.5" /> Clear signature
      </button>
    </div>
  );
}
